import { useEffect, useState, useRef } from "react";
import io from "socket.io-client";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "../styles/AlertsPage.css";

const WS_URL = process.env.REACT_APP_WS_URL || "http://localhost:5000";
const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
const MAPBOX_TOKEN =
  process.env.REACT_APP_MAPBOX_TOKEN ||
  "pk.eyJ1IjoibWFwbGFwYW5lIiwiYSI6ImNtaDN5aGwybDFnY3AwOHFwbzJrOTF4c2cifQ.sQB_UMSe27x9JRdllElxmw";

const alertIcon = new L.Icon({
  iconUrl: "/alert_pin.png",
  iconSize: [24, 32],
  iconAnchor: [12, 32],
});

const severityColors = {
  CRITICAL: "bg-red-600 text-white",
  WARNING: "bg-amber-500 text-black",
  INFO: "bg-blue-500 text-white",
};

export default function AlertsPage() {
  const [alerts, setAlerts] = useState([]);
  const [filter, setFilter] = useState("ALL");
  const socketRef = useRef(null);

  // --- Fetch initial alerts ---
  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        const res = await fetch(`${API_URL}/v1/alerts?limit=100`);
        const data = await res.json();
        setAlerts(data);
      } catch (err) {
        console.error("Failed to fetch alerts:", err);
      }
    };
    fetchAlerts();
  }, []);

  // --- Live socket feed ---
  useEffect(() => {
    socketRef.current = io(WS_URL, {
      path: "/socket.io",
      transports: ["websocket", "polling"],
    });

    socketRef.current.on("connect", () =>
      console.log("Connected to socket:", socketRef.current.id)
    );

    socketRef.current.on("alert", (alert) => {
      setAlerts((prev) => [alert, ...prev.slice(0, 99)]);
    });

    socketRef.current.on("alert_update", (update) => {
      setAlerts((prev) =>
        prev.map((a) =>
          a.id === update.id ? { ...a, status: update.status } : a
        )
      );
    });

    return () => socketRef.current.disconnect();
  }, []);

  // --- Update alert status ---
  const handleStatusUpdate = async (id, status) => {
    try {
      const res = await fetch(`${API_URL}/v1/alerts/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      const { alert } = await res.json();
      setAlerts((prev) =>
        prev.map((a) => (a.id === id ? { ...a, status: alert.status } : a))
      );
    } catch (err) {
      console.error("Status update failed:", err);
    }
  };

  const filtered =
    filter === "ALL" ? alerts : alerts.filter((a) => a.severity === filter);

  return (
    <div className="alerts-layout">
      {/* ---- Left Feed Panel ---- */}
      <div className="alerts-feed">
        {/* Sticky Header */}
        <div className="feed-header">
          <h2 className="feed-title">Fleet Alerts Feed</h2>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="filter-select"
          >
            <option value="ALL">All</option>
            <option value="CRITICAL">Critical</option>
            <option value="WARNING">Warning</option>
            <option value="INFO">Info</option>
          </select>
        </div>

        {/* Scrollable list */}
        <div className="feed-list">
          {filtered.length === 0 && (
            <p className="text-gray-500 text-sm mt-3">No alerts yet.</p>
          )}
          {filtered.map((alert) => (
            <div key={alert.id} className="alert-card">
              <div
                className={`alert-bar ${
                  alert.severity === "CRITICAL"
                    ? "bg-red-500"
                    : alert.severity === "WARNING"
                    ? "bg-amber-400"
                    : "bg-blue-400"
                }`}
              />
              <div className="alert-header">
                <h3 className="alert-title">{alert.title}</h3>
                <span
                  className={`alert-severity ${
                    severityColors[alert.severity] || "bg-gray-600"
                  }`}
                >
                  {alert.severity}
                </span>
              </div>
              <p className="alert-message">{alert.message}</p>

              <div className="alert-footer">
                <span>
                  {alert.vehicle?.name ??
                    alert.vehicleName ??
                    `Vehicle ${alert.vehicleId}`}{" "}
                  • {new Date(alert.createdAt).toLocaleTimeString()}
                </span>
                <div className="alert-actions">
                  {alert.status !== "Acknowledged" && (
                    <button
                      onClick={() =>
                        handleStatusUpdate(alert.id, "Acknowledged")
                      }
                      className="btn-ack"
                    >
                      Ack
                    </button>
                  )}
                  {alert.status !== "Resolved" && (
                    <button
                      onClick={() => handleStatusUpdate(alert.id, "Resolved")}
                      className="btn-resolve"
                    >
                      Resolve
                    </button>
                  )}
                </div>
              </div>

              <div className="alert-status">
                Status:{" "}
                <span
                  className={`${
                    alert.status === "Resolved"
                      ? "text-green-400"
                      : alert.status === "Acknowledged"
                      ? "text-blue-400"
                      : "text-red-400"
                  }`}
                >
                  {alert.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ---- Right Map Panel ---- */}
      <div className="alerts-map">
        <MapContainer
          center={[-28.6, 24.657847]}
          zoom={6}
          style={{ height: "100%", width: "100%" }}
          zoomControl={true}
        >
          <TileLayer
            url={`https://api.mapbox.com/styles/v1/mapbox/light-v11/tiles/{z}/{x}/{y}?access_token=${MAPBOX_TOKEN}`}
          />
          {filtered.map(
            (a) =>
              a.lat &&
              a.lon && (
                <Marker key={a.id} position={[a.lat, a.lon]} icon={alertIcon}>
                  <Popup>
                    <b>{a.title}</b>
                    <br />
                    {a.message}
                    <br />
                    Vehicle: {a.vehicleName ?? `#${a.vehicleId}`}
                    <br />
                    {a.severity} @{" "}
                    {new Date(a.createdAt).toLocaleTimeString()}
                    <br />
                    Status: {a.status}
                  </Popup>
                </Marker>
              )
          )}
        </MapContainer>
      </div>
    </div>
  );
}
